import TimeOff from "../../entities/timeOff";

/**
 * TimeOff actions
 *
 * Call order:
 *
 * TIMEOFF_SAVE_QUEUE:	adds the item to the save queue
 * --> TIMEOFF_SAVE:	modifies the state
 * --> TIMEOFF_SAVE_SUCCESS: if all went well
 * --> TIMEOFF_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail TIMEOFF_SAVE_QUEUE is during the call to TIMEOFF_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at TIMEOFF_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: TIMEOFF_SAVE_SUCCESS, TIMEOFF_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or TIMEOFF_PENDING_CHECK (it left the queue).
 */

export const TIMEOFF_FETCH_ONE_QUEUE: any = 'TIMEOFF_FETCH_ONE_QUEUE';
export const TIMEOFF_FETCH_SUCCESS: any = 'TIMEOFF_FETCH_SUCCESS';
export const TIMEOFF_FETCH_FAILURE: any = 'TIMEOFF_FETCH_FAILURE';
export const TIMEOFF_FETCH_ONE_SUCCESS: any = 'TIMEOFF_FETCH_ONE_SUCCESS';
export const TIMEOFF_FETCH_ONE_FAILURE: any = 'TIMEOFF_FETCH_ONE_FAILURE';

export const TIMEOFF_SAVE: any = 'TIMEOFF_SAVE';
export const TIMEOFF_SAVE_QUEUE: any = 'TIMEOFF_SAVE_QUEUE';
export const TIMEOFF_SAVE_SUCCESS: any = 'TIMEOFF_SAVE_SUCCESS';
export const TIMEOFF_SAVE_FAILURE: any = 'TIMEOFF_SAVE_FAILURE';

export const TIMEOFF_SAVE_MANY: any = 'TIMEOFF_SAVE_MANY';
export const TIMEOFF_SAVE_MANY_QUEUE: any = 'TIMEOFF_SAVE_MANY_QUEUE';
export const TIMEOFF_SAVE_MANY_SUCCESS: any = 'TIMEOFF_SAVE_MANY_SUCCESS';
export const TIMEOFF_SAVE_MANY_FAILURE: any = 'TIMEOFF_SAVE_MANY_FAILURE';

export const TIMEOFF_REMOVE: any = 'TIMEOFF_REMOVE';
export const TIMEOFF_REMOVE_QUEUE: any = 'TIMEOFF_REMOVE_QUEUE';
export const TIMEOFF_REMOVE_SUCCESS: any = 'TIMEOFF_REMOVE_SUCCESS';
export const TIMEOFF_REMOVE_FAILURE: any = 'TIMEOFF_REMOVE_FAILURE';

export const TIMEOFF_RESTORE: any = 'TIMEOFF_RESTORE';
export const TIMEOFF_RESTORE_QUEUE: any = 'TIMEOFF_RESTORE_QUEUE';
export const TIMEOFF_RESTORE_SUCCESS: any = 'TIMEOFF_RESTORE_SUCCESS';
export const TIMEOFF_RESTORE_FAILURE: any = 'TIMEOFF_RESTORE_FAILURE';

export const TIMEOFF_RESTORE_MANY: any = 'TIMEOFF_RESTORE_MANY';
export const TIMEOFF_REMOVE_MANY: any = 'TIMEOFF_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const TIMEOFF_SAVE_REVERT: any = 'TIMEOFF_SAVE_REVERT';
export const TIMEOFF_PENDING_CHECK: any = 'TIMEOFF_PENDING_CHECK';

/* Fetch actions */
export const fetchTimeOffSuccess = (timeOffs: TimeOff[], partial: boolean, lastFetch: number) => ({type: TIMEOFF_FETCH_SUCCESS, timeOffs, partial, lastFetch});
export const fetchTimeOffFailure = (err: any) => {
		console.error('TimeOffAction: fetchTimeOffFailure', err);
		return {type: TIMEOFF_FETCH_FAILURE, err};
};
export const fetchOneTimeOffQueue = (timeOffId: string, sentAddedOn?: number) => ({type: TIMEOFF_FETCH_ONE_QUEUE, timeOffId, sentAddedOn});
export const fetchOneTimeOffSuccess = (timeOff: TimeOff | null, sentAddedOn?: number, timeOffId: string = timeOff?.id) => ({type: TIMEOFF_FETCH_ONE_SUCCESS, timeOffId, timeOff, sentAddedOn});
export const fetchOneTimeOffFailure = (id: string, err: any) => {
		console.error('TimeOffAction: fetchOneTimeOffFailure', err);
		return {type: TIMEOFF_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveTimeOff = (timeOff: TimeOff) => {
    // console.debug('⮂ TIMEOFF_SAVE action', timeOff?.id);
    return {type: TIMEOFF_SAVE, timeOff};
}
export const saveTimeOffQueue = (timeOff: TimeOff, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ TIMEOFF_SAVE_QUEUE action', timeOff?.id);
    return {type: TIMEOFF_SAVE_QUEUE, timeOff, onQueued};
}
export const saveTimeOffSuccess = (timeOff: TimeOff, sentAddedOn?: number) => {
    // console.debug('⮂ TIMEOFF_SAVE_SUCCESS action', timeOff?.id);
    return {type: TIMEOFF_SAVE_SUCCESS, timeOff, sentAddedOn};
}
export const saveTimeOffFailure = (timeOff: TimeOff, status: number, message: string) => {
    console.warn(`⮂ TIMEOFF_SAVE_FAILURE error saving timeOff: HTTP ${status}: ${message}`, timeOff?.id);
    return {type: TIMEOFF_SAVE_FAILURE, timeOff, status, message};
};

/* SaveMany actions */
export const saveManyTimeOffs = (timeOffs: TimeOff[]) => {
    // console.debug('⮂ TIMEOFF_SAVE_MANY action', timeOffs?.map(timeOff => timeOff?.id).join(','));
    return {type: TIMEOFF_SAVE_MANY, timeOffs};
}
export const saveManyTimeOffsQueue = (timeOffs: TimeOff[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ TIMEOFF_SAVE_MANY_QUEUE action', timeOffs?.map(timeOff => timeOff.id).join(','));
    return {type: TIMEOFF_SAVE_MANY_QUEUE, timeOffs, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `timeOffs` (OWA-1018) */
export const saveManyTimeOffsSuccess = (timeOffs: TimeOff[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ TIMEOFF_SAVE_MANY_SUCCESS action', timeOffs?.map(timeOff => timeOff.id).join(','));
    return {type: TIMEOFF_SAVE_MANY_SUCCESS, timeOffs, sentAddedOns};
}
export const saveManyTimeOffsFailure = (timeOffs: TimeOff[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_TIMEOFFS_FAILURE Error saving timeOffs: HTTP ${status}: ${message}`, timeOffs?.map(timeOff => timeOff.id).join(','));
		return {type: TIMEOFF_SAVE_MANY_FAILURE, timeOffs, status, message};
};

/* Remove actions */
export const removeTimeOff = (timeOff: TimeOff) => ({ type: TIMEOFF_REMOVE, timeOff });
export const removeTimeOffQueue = (timeOff: TimeOff) => ({ type: TIMEOFF_REMOVE_QUEUE, timeOff });
export const removeTimeOffSuccess = (timeOff: TimeOff) => ({ type: TIMEOFF_REMOVE_SUCCESS, timeOff });
export const removeTimeOffFailure = (timeOff: TimeOff, status: number, message: string) => {
	console.error(`Redux: removeTimeOffFailure Error removing timeOff: HTTP ${status}: ${message}`);
	return ({ type: TIMEOFF_REMOVE_FAILURE, timeOff, status, message });
};

/* Restore actions */
export const restoreTimeOff = (timeOff: TimeOff) => ({ type: TIMEOFF_RESTORE, timeOff });
export const restoreTimeOffQueue = (timeOff: TimeOff) => ({ type: TIMEOFF_RESTORE_QUEUE, timeOff });
export const restoreTimeOffSuccess = (timeOff: TimeOff, sentAddedOn?: number) => ({ type: TIMEOFF_RESTORE_SUCCESS, timeOff, sentAddedOn });
export const restoreTimeOffFailure = (timeOff: TimeOff, status: number, message: string) => {
	console.error(`Redux: restoreTimeOffFailure Error restoring timeOff: HTTP ${status}: ${message}`);
	return ({ type: TIMEOFF_RESTORE_FAILURE, timeOff, status, message });
};

/* RestoreMany actions */
export const restoreManyTimeOffs = (timeOffs: TimeOff[]) => ({ type: TIMEOFF_RESTORE_MANY, timeOffs });

/* RemoveMany actions */
export const removeManyTimeOffs = (timeOffs: TimeOff[]) => ({ type: TIMEOFF_REMOVE_MANY, timeOffs });

/* Pending save actions (OWA-1018) */
/**
 * The save `timeOff` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertTimeOffSave = (timeOff: TimeOff, options: {sentAddedOn?: number, current?: TimeOff | null, keep?: boolean} = {}) =>
    ({type: TIMEOFF_SAVE_REVERT, timeOff, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkTimeOffPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: TIMEOFF_PENDING_CHECK, live, refused, readAt});